if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;
      
      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 0));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });

      // Prev/next arrows overlaid on the main media (Desktop navigation style = Arrows).
      // Step through media in thumbnail order, updating the featured media and the
      // active thumbnail; loops at the ends. No-op when the arrows aren't rendered.
      this.mediaNavButtons = this.elements.viewer.querySelectorAll('.product__media-nav');
      this.mediaNavButtons.forEach((button) => {
        const direction = button.classList.contains('product__media-nav--prev') ? -1 : 1;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.stepMedia(direction);
        });
      });
      
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();

      this.stackedThumbnails = this.dataset.desktopLayout === 'stacked' && this.elements.thumbnails.classList.contains('thumbnail-slider--stacked');
      if (this.stackedThumbnails) this.observeStackedMedia();

      const clientWidth = Math.round(document.body.clientWidth);
      const mainSlieder = this.elements?.viewer?.slider;

      if (mainSlieder && clientWidth < 750 && this.dataset.mobileLayout != 'stacked') {
        const currentMedia = this.firstVisibleProductMedia();
        mainSlieder.style.height = `${currentMedia?.clientHeight}px` || 'auto';
      }

      window.addEventListener('resize', () => {
        const clientWidth = Math.round(document.body.clientWidth);
        if (clientWidth < 750 && this.dataset.mobileLayout != 'stacked') {
          const currentMedia = this.firstVisibleProductMedia();
          if (mainSlieder) mainSlieder.style.height = `${currentMedia?.clientHeight}px` || 'auto';
        } else {
          if (mainSlieder) mainSlieder.style.height = 'unset';
        }
      });
    }

    // --- Variant image groups -------------------------------------------------
    // Feature is active only when the server emitted a non-empty group map on
    // <media-gallery data-variant-media-groups="{...}">. Everything below is a
    // no-op otherwise, so galleries without the feature are unaffected.
    get hasVariantMediaGroups() {
      const raw = this.dataset.variantMediaGroups;
      return raw != null && raw !== '' && raw !== '{}';
    }

    get variantMediaGroups() {
      if (this._variantMediaGroups) return this._variantMediaGroups;
      try {
        this._variantMediaGroups = JSON.parse(this.dataset.variantMediaGroups || '{}');
      } catch (e) {
        this._variantMediaGroups = {};
      }
      return this._variantMediaGroups;
    }

    // data-media-id / data-target are "<sectionId>-<mediaId>"; the media id is the
    // numeric segment after the final hyphen (section ids may contain hyphens).
    variantMediaBareId(prefixed) {
      if (prefixed == null) return '';
      const str = String(prefixed);
      const i = str.lastIndexOf('-');
      return i >= 0 ? str.slice(i + 1) : str;
    }

    // Product order (data-media-position), not DOM order: after a reorder the DOM
    // carries the previous variant's order, and a no-group variant with fallback
    // "all" must still show the product's own order.
    variantMediaAllIds() {
      if (!this.elements.viewer) return [];
      // Slides only: the zoom opener and .product-media inside each slide carry a
      // bare data-media-id too, so a plain [data-media-id] query returns each
      // media three times.
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'))
        .sort((a, b) => (Number(a.dataset.mediaPosition) || 0) - (Number(b.dataset.mediaPosition) || 0))
        .map((el) => this.variantMediaBareId(el.dataset.mediaId));
    }

    // First slide that isn't grouped out (the mobile slider's height follows it).
    firstVisibleProductMedia() {
      const viewer = this.elements?.viewer;
      if (!viewer) return null;
      return viewer.querySelector('.product__media-item:not(.product__media-item--grouped-out) .product-media')
        || viewer.querySelector('.product-media');
    }

    // SliderComponent caches sliderItems / sliderItemsToShow at construction and
    // resetPages() re-reads every [id^="Slide-"] including hidden ones, so after a
    // group is applied its page count and slideChanged currentElement would be
    // stale (a hidden slide → 0px mobile height). Hand it the visible, ordered
    // items directly instead.
    syncSliderItems(component, hiddenClass) {
      if (!component || !component.slider) return;
      const items = Array.from(component.querySelectorAll('[id^="Slide-"]'))
        .filter((item) => !item.classList.contains(hiddenClass));
      component.sliderItems = items;
      component.sliderItemsToShow = items;
      if (typeof component.getTotalPages === 'function') component.totalPages = component.getTotalPages();
      component.currentPage = 1;
      component._lastComputedPage = 1;
      if (this.mql.matches) {
        const hide = component.totalPages < 2;
        if (component.prevButtonArrow) component.prevButtonArrow.style.display = hide ? 'none' : '';
        if (component.nextButtonArrow) component.nextButtonArrow.style.display = hide ? 'none' : '';
      }
    }

    // The ordered list of media ids that should be visible for a variant, per the
    // plan: the variant's own group (in order), then — if "shared" is on — every
    // media not assigned to any group, in product order; or the fallback set when
    // the variant has no group.
    variantMediaVisibleIds(variantId) {
      const groups = this.variantMediaGroups;
      const all = this.variantMediaAllIds();
      const assigned = new Set();
      Object.keys(groups).forEach((vid) => (groups[vid] || []).forEach((id) => assigned.add(String(id))));
      const shared = (this.dataset.variantMediaShared || 'true') !== 'false';
      const fallback = this.dataset.variantMediaFallback || 'all';
      const group = groups[String(variantId)];
      let ids;
      if (group && group.length) {
        ids = group.map(String).filter((id) => all.indexOf(id) !== -1);
        if (shared) all.forEach((id) => { if (!assigned.has(id)) ids.push(id); });
      } else if (fallback === 'shared') {
        ids = all.filter((id) => !assigned.has(id));
      } else {
        ids = all.slice();
      }
      return ids;
    }

    // Apply a variant's group to the gallery. activate=false (initial sync) only
    // reorders/toggles visibility and syncs the zoom — the server already set
    // is-active, and activating here would scroll the page. activate=true
    // (variant change) also moves the active slide.
    applyVariantMedia(variantId, options) {
      if (!this.hasVariantMediaGroups) return;
      const viewer = this.elements.viewer;
      if (!viewer) return;
      const opts = options || {};
      const activate = opts.activate === true;
      const thumbs = this.elements.thumbnails;
      const sectionId = this.id.replace('MediaGallery-', '');

      const visibleIds = Array.from(new Set(this.variantMediaVisibleIds(variantId)));
      const visibleSet = new Set(visibleIds);

      // Visibility
      viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((slide) => {
        const bare = this.variantMediaBareId(slide.dataset.mediaId);
        slide.classList.toggle('product__media-item--grouped-out', !visibleSet.has(bare));
      });
      if (thumbs) {
        thumbs.querySelectorAll('[data-target]').forEach((thumb) => {
          const bare = this.variantMediaBareId(thumb.dataset.target);
          thumb.classList.toggle('thumbnail-list__item--grouped-out', !visibleSet.has(bare));
        });
      }

      // Order: reverse-prepend so the first visible id ends up first and the rest
      // keep their relative order (generalises setActiveMedia's prepend).
      for (let i = visibleIds.length - 1; i >= 0; i--) {
        const id = visibleIds[i];
        const slide = viewer.querySelector(`.product__media-item[data-media-id="${sectionId}-${id}"]`);
        if (slide && slide.parentElement) slide.parentElement.prepend(slide);
        if (thumbs) {
          const thumb = thumbs.querySelector(`[data-target="${sectionId}-${id}"]`);
          if (thumb && thumb.parentElement) thumb.parentElement.prepend(thumb);
        }
      }

      // Pagination from the now-visible items (see syncSliderItems).
      this.syncSliderItems(viewer, 'product__media-item--grouped-out');
      if (thumbs) this.syncSliderItems(thumbs, 'thumbnail-list__item--grouped-out');

      // Mobile: the list is a scroll-snap slider; a single visible slide must not
      // keep the 90% "peek" width, and the slider height follows the first slide.
      const list = viewer.querySelector('.product__media-list');
      if (list) list.classList.toggle('product__media-count-1', visibleIds.length === 1);
      // Prev/next over the featured media are rendered for product.media.size > 1;
      // a one-image group has nothing to step to.
      (this.mediaNavButtons || []).forEach((button) => { button.style.display = visibleIds.length < 2 ? 'none' : ''; });
      if (!this.mql.matches && this.dataset.mobileLayout != 'stacked' && viewer.slider) {
        viewer.slider.scrollTo({ left: 0 });
        const firstMedia = this.firstVisibleProductMedia();
        if (firstMedia) viewer.slider.style.height = `${firstMedia.clientHeight}px`;
      }

      // Mobile counter total. onSlideChanged keeps it in sync after swipes.
      const counter = document.querySelector('.mobile-product-counter');
      if (counter) counter.innerHTML = `1/${visibleIds.length}`;

      // Zoom follows the group (order + visible set), on init and on change.
      const zoom = document.getElementById(`ProductZoom-${sectionId}`);
      if (zoom && typeof zoom.setVisible === 'function') zoom.setVisible(visibleIds);

      if (activate) {
        // Layouts that show every slide at once scroll the page to the active
        // slide; there the gallery's first (group-order) slide is the target, as
        // the legacy prepend path did. The native featured media only decides
        // which slide is shown in the one-at-a-time slider layouts.
        const allVisible = this.mql.matches
          ? ['stacked', 'columns', 'mosaic'].includes(this.dataset.desktopLayout)
          : this.dataset.mobileLayout === 'stacked';
        const featured = opts.featuredMediaId != null ? String(opts.featuredMediaId) : null;
        const activeId = (!allVisible && featured && visibleSet.has(featured)) ? featured : visibleIds[0];
        if (activeId != null) this.setActiveMedia(`${sectionId}-${activeId}`, false);
      } else if (thumbs) {
        // Init: the server set is-active; make sure aria-current sits on that
        // slide's thumbnail (the legacy markup marks the featured media's).
        const active = viewer.querySelector('[data-media-id].is-active');
        const thumb = active && thumbs.querySelector(`[data-target="${active.dataset.mediaId}"]`);
        if (thumb && thumb.querySelector('button')?.getAttribute('aria-current') !== 'true') this.setActiveThumbnail(thumb);
      }
    }
    // --------------------------------------------------------------------------

    onSlideChanged(event) {
      const mediaId = event.detail?.currentElement?.dataset?.mediaId || '';
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      
      const productCounter = document.querySelector('.mobile-product-counter');
      
      if (productCounter) {
          productCounter.innerHTML = `${event.detail.currentPage}/${event.detail.totalPages}`
      }

      const clientWidth = Math.round(document.body.clientWidth);
      if (clientWidth < 750 && activeMedia && !activeMedia.classList.contains('product__media-item--grouped-out')) {
        const currentMedia = activeMedia.querySelector('.product-media');
        const mainSlieder = this.elements?.viewer?.slider;
        
        if (mainSlieder) mainSlieder.style.height = `${currentMedia?.clientHeight}px` || 'auto';
      }
      // if (!thumbnail) return;
      // this.setActiveThumbnail(thumbnail);
    }

    stepMedia(direction) {
      if (!this.elements.thumbnails) return;
      const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
        .filter((item) => !item.classList.contains('thumbnail-list__item--grouped-out'));
      if (!items.length) return;
      let index = items.findIndex((item) => item.querySelector('button')?.getAttribute('aria-current') === 'true');
      if (index === -1) {
        const active = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (active) index = items.findIndex((item) => item.dataset.target === active.dataset.mediaId);
      }
      if (index === -1) index = 0;
      const nextIndex = (index + direction + items.length) % items.length;
      const target = items[nextIndex].dataset.target;
      if (target) this.setActiveMedia(target, false);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      if (this.dataset.desktopLayout == 'thumbnail_bottom_2') {
        const nextMediaElement = activeMedia.nextElementSibling;
        if (nextMediaElement) {
          nextMediaElement.classList.add('is-active');
        } else {
          const prevMediaElement = activeMedia.previousElementSibling;
          if (prevMediaElement) prevMediaElement.classList.add('is-active');
        }
      }
      
      activeMedia.classList.add('is-active');

      activeMedia.classList.add('media-slide-in');
      activeMedia.addEventListener('animationend', () => {
        activeMedia.classList.remove('media-slide-in');
      }, { once: true });

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.stackedThumbnails && this.mql.matches) {
          // Only scroll the page for user-initiated changes (thumbnail click, arrows).
          // First load / variant sync call this with prepend=true (and prepend the
          // featured media to the top); scrolling there would yank a shopper who has
          // already scrolled down back to the top of the page.
          if (!prepend) this.scrollToStackedMedia(activeMedia);
          return;
        }
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);

      // Keep the zoom view on the same image so it opens on the right one next.
      const zoom = document.getElementById(`ProductZoom-${this.id.replace('MediaGallery-', '')}`);
      if (zoom && typeof zoom.setActive === 'function') {
        const opener = activeMedia.querySelector('.opener');
        if (opener) zoom.setActive(opener.dataset.mediaId);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.stackedThumbnails && this.mql.matches) {
        this.revealStackedThumbnail(thumbnail);
        return;
      }
      if (this.elements.thumbnails.slider && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    // Stacked layout with thumbnails: the page scrolls, the strip stays put
    scrollToStackedMedia(media) {
      const offset = parseFloat(getComputedStyle(this.elements.thumbnails).top) || 0;
      const top = media.getBoundingClientRect().top + window.scrollY - offset;
      this.suppressStackedTracking = Date.now() + 800;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    revealStackedThumbnail(thumbnail) {
      const list = this.elements.thumbnails.slider;
      if (!list) return;
      const top = thumbnail.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      const bottom = top + thumbnail.offsetHeight;
      if (top >= list.scrollTop && bottom <= list.scrollTop + list.clientHeight) return;
      list.scrollTo({ top: top - (list.clientHeight - thumbnail.offsetHeight) / 2 });
    }

    observeStackedMedia() {
      const items = this.elements.viewer.querySelectorAll('[data-media-id]');
      if (!items.length || !('IntersectionObserver' in window)) return;
      const ratios = new Map();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
        if (!this.mql.matches || Date.now() < (this.suppressStackedTracking || 0)) return;
        let best = null;
        ratios.forEach((ratio, item) => {
          // Grouped-out media is display:none (ratio 0), but guard explicitly so a
          // hidden item is never chosen as the active one.
          if (ratio > 0 && !item.classList.contains('product__media-item--grouped-out') && (!best || ratio > ratios.get(best))) best = item;
        });
        if (!best) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${best.dataset.mediaId}"]`);
        if (thumbnail && thumbnail.querySelector('button').getAttribute('aria-current') !== 'true') this.setActiveThumbnail(thumbnail);
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
      items.forEach((item) => observer.observe(item));
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);

      if (this.dataset.autoplayVideo === 'true') {
        window.setTimeout(() => {
          const nativeVideo = activeItem.querySelector('video');
          if (nativeVideo) {
            nativeVideo.play();
            return;
          }
          const iframe = activeItem.querySelector('iframe');
          if (iframe) {
            const src = iframe.src || '';
            if (src.includes('youtube.com') || src.includes('youtu.be')) {
              iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            } else if (src.includes('vimeo.com')) {
              iframe.contentWindow.postMessage('{"method":"play"}', '*');
            }
          }
        }, 100);
      }
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
